import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import PlainSelect from '../PlainSelect'
import { ReduxEntitySelect } from 'components/FormElements'
// hooks
import { useTemplateFields } from 'hooks/useTemplateFields'
// constants
import { Forms } from 'constants/forms'
import { AGGREGATE_OPTIONS, CREATED_AT_FIELD_ID, DATE_BUCKET_OPTIONS, GROUPABLE_FIELD_TYPES } from 'constants/charts'
// graphql
import { RISK_TEMPLATE_SELECT_QUERY, RISK_VIEW_FILTERS_SELECT_QUERY } from 'graphql/risk/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { ChartType } from 'types/charts'
import { ChartAggregate, ChartDateBucket, FieldType, FilterTarget, IFilter, IRiskTemplate } from 'types/graphql'
// views
import { Row } from '../../views'
// utils
import { required } from 'utils/validators'
import { map, pipe } from 'lodash/fp'
import { safeGetOr } from 'utils/fp'

const selector = formValueSelector(Forms.DASHBOARD_WIDGET_SETTINGS_FORM)

const QUERY = 'chartConfig.query'

const getTemplatesOptions = pipe(
  safeGetOr<IRiskTemplate, any, IRiskTemplate[]>([], 'data.riskTemplates.data'),
  map<IRiskTemplate, IOption>(({ id, name, archived }) => ({ label: name, value: id, isDisabled: archived }))
)

const getFiltersOptions = pipe(
  safeGetOr<IFilter, any, IFilter[]>([], 'data.filters.data'),
  map<IFilter, IOption>(({ id, name }) => ({ label: name || '', value: id }))
)

// A group-by select per label: as many as the type takes at most (GROUP_BY_COUNTS)
const GROUP_LABELS: Record<ChartType, string[]> = {
  [ChartType.Bar]: ['Categories (axis)', 'Split into series by'],
  [ChartType.Line]: ['Categories (axis)', 'Split into lines by'],
  [ChartType.Pie]: ['Slices by']
}

interface Props {
  chartType: ChartType
}

const DataFields: React.FC<Props> = ({ chartType }) => {
  const dispatch = useDispatch()

  const riskTemplate: IOption = useSelector((state) => selector(state, 'riskTemplateId'))
  const groupBy = useSelector((state) => selector(state, `${QUERY}.groupBy`)) || []
  const aggregate = useSelector((state) => selector(state, `${QUERY}.aggregate`))

  const { fields } = useTemplateFields(riskTemplate?.value)

  const activeFields = fields.filter((field) => field.active !== false)

  const groupOptions: IOption[] = [
    { label: 'Created date', value: CREATED_AT_FIELD_ID },
    ...activeFields
      .filter((field) => GROUPABLE_FIELD_TYPES.includes(field.fieldType))
      .map((field) => ({ label: field.label, value: field.id }))
  ]

  const numberOptions: IOption[] = activeFields
    .filter((field) => field.fieldType === FieldType.Number)
    .map((field) => ({ label: field.label, value: field.id }))

  const isDateGroup = (fieldId?: string) =>
    fieldId === CREATED_AT_FIELD_ID || fields.find((field) => field.id === fieldId)?.fieldType === FieldType.Date

  const setValue = (name: string, value: unknown) => dispatch(change(Forms.DASHBOARD_WIDGET_SETTINGS_FORM, name, value))

  const onTemplateChange = () => {
    setValue('filterId', null)
    setValue(`${QUERY}.groupBy`, [])
    setValue(`${QUERY}.aggregateFieldId`, null)
  }

  const onGroupFieldChange = (index: number) => (option: IOption | null) => {
    setValue(`${QUERY}.groupBy[${index}].dateBucket`, isDateGroup(option?.value) ? ChartDateBucket.Month : null)
  }

  return (
    <>
      <ReduxEntitySelect
        label='Form'
        name='riskTemplateId'
        validate={required}
        getOptions={getTemplatesOptions}
        query={RISK_TEMPLATE_SELECT_QUERY}
        onSelectChange={onTemplateChange}
        handleSearch={({ search, refetch }) => refetch({ filter: { name: search } })}
      />
      <ReduxEntitySelect
        key={`filter-${riskTemplate?.value}`}
        isClearable
        label='Filter'
        name='filterId'
        skip={!riskTemplate}
        isDisabled={!riskTemplate}
        getOptions={getFiltersOptions}
        query={RISK_VIEW_FILTERS_SELECT_QUERY}
        // A chart counts by shared filters only: a personal one is CHART_FILTER_NOT_FOUND
        variables={{ filter: { targetId: riskTemplate?.value, target: FilterTarget.RiskTemplate, global: true } }}
      />
      {GROUP_LABELS[chartType].map((label, index) => {
        const fieldId = groupBy[index]?.fieldId
        const isHidden = index > 0 && !groupBy[0]?.fieldId

        if (isHidden) {
          return null
        }

        return (
          <Row key={index}>
            {/* Every chart type groups by one field at least: only the split into series may be left empty */}
            <PlainSelect
              isClearable={index > 0}
              validate={index === 0 ? required : undefined}
              label={label}
              name={`${QUERY}.groupBy[${index}].fieldId`}
              options={groupOptions}
              isDisabled={!riskTemplate}
              onSelectChange={onGroupFieldChange(index)}
            />
            {isDateGroup(fieldId) && (
              <PlainSelect
                label='Period'
                name={`${QUERY}.groupBy[${index}].dateBucket`}
                options={DATE_BUCKET_OPTIONS}
              />
            )}
          </Row>
        )
      })}
      <Row>
        <PlainSelect label='Value' name={`${QUERY}.aggregate`} options={AGGREGATE_OPTIONS} />
        {aggregate && aggregate !== ChartAggregate.Count && (
          <PlainSelect
            label='Number field'
            validate={required}
            name={`${QUERY}.aggregateFieldId`}
            options={numberOptions}
            isDisabled={!riskTemplate}
          />
        )}
      </Row>
    </>
  )
}

export default DataFields
